import { useMemo, useState } from 'react';
import { TableCell, TableSortLabel, type TableCellProps } from '@mui/material';

/**
 * Ordenamiento y filtro de tabla, en un solo lugar.
 *
 * ─── POR QUÉ COMPARTIDO ─────────────────────────────────────────────────────
 *
 * La app tiene 34 tablas y seis ordenan; cada una con su propia copia de
 * `sortKey` / `sortDir` / `toggleSort`. Copiar el patrón una séptima vez no es
 * el problema —son ocho líneas—: el problema es que cada copia decide por su
 * cuenta qué hacer con los valores que faltan, y ahí es donde una tabla miente.
 *
 * ─── LA REGLA QUE NINGUNA COPIA ACIERTA SOLA ────────────────────────────────
 *
 * UN VALOR AUSENTE NO ES UN VALOR CHICO. Si `null` se ordena como cero, pedir
 * "peor cobertura primero" pone arriba a los cursos que todavía no tienen nada
 * que medir y esconde a los que sí están mal. Acá lo ausente va SIEMPRE al
 * final, suba o baje el resto — es contexto, no un extremo del ranking.
 *
 * Los textos se comparan con `localeCompare`: en español, sin eso, "Ñ" y los
 * acentos quedan fuera de lugar.
 */

export type SortDirection = 'asc' | 'desc';

/** Marcas diacríticas: lo que queda al descomponer un acento con NFD. */
const ACCENTS = /[\u0300-\u036f]/g;

/** Cómo se saca de la fila el valor por el que se ordena. */
export type SortAccessor<T> = (row: T) => string | number | null | undefined;

/**
 * El comparador, como función pura y exportada: es la regla que importa y la
 * que se prueba. Los hooks de abajo sólo le ponen estado de React encima.
 */
export function compareBy<T>(
  accessor: SortAccessor<T>,
  dir: SortDirection,
): (a: T, b: T) => number {
  const factor = dir === 'asc' ? 1 : -1;
  return (a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    // Lo ausente al final SIEMPRE, sin importar la dirección.
    const aMissing = av === null || av === undefined || av === '';
    const bMissing = bv === null || bv === undefined || bv === '';
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
    return String(av).localeCompare(String(bv), 'es') * factor;
  };
}

/** Normaliza acentos y mayusculas en los DOS lados de la comparacion. */
export function normalizeForSearch(value: string): string {
  return value.normalize('NFD').replace(ACCENTS, '').toLowerCase();
}

/** Alguno de los campos de la fila contiene lo buscado? */
export function matchesQuery(fields: (string | undefined)[], query: string): boolean {
  const q = normalizeForSearch(query.trim());
  if (!q) return true;
  return fields.some((value) => value !== undefined && normalizeForSearch(value).includes(q));
}

export interface UseTableSortResult<T> {
  rows: T[];
  sortKey: string | null;
  sortDir: SortDirection;
  toggle: (key: string) => void;
}

export function useTableSort<T>(
  rows: T[],
  accessors: Record<string, SortAccessor<T>>,
  /** `key: null` = NO ordenar: se respeta el orden en que vinieron las filas.
   *
   *  Existe porque un orden calculado en el servidor puede ser parte del
   *  informe. En cobertura de registro lo es: las secciones sin trabajo cargado
   *  van primero por decisión, y un orden inicial por columna lo descartaba —
   *  el peor caso volvía a quedar escondido entre los que van bien. */
  initial: { key: string | null; dir?: SortDirection },
): UseTableSortResult<T> {
  const [sortKey, setSortKey] = useState<string | null>(initial.key);
  const [sortDir, setSortDir] = useState<SortDirection>(initial.dir ?? 'asc');

  const toggle = (key: string): void => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const accessor = sortKey === null ? undefined : accessors[sortKey];
    return accessor ? [...rows].sort(compareBy(accessor, sortDir)) : rows;
  }, [rows, accessors, sortKey, sortDir]);

  return { rows: sorted, sortKey, sortDir, toggle };
}

/**
 * Encabezado ordenable. Envuelve `TableSortLabel` para que ninguna tabla tenga
 * que acordarse de pasar `active` y `direction` en la combinación correcta.
 */
export function SortableCell<T>({
  columnKey,
  sort,
  children,
  ...cellProps
}: {
  columnKey: string;
  sort: UseTableSortResult<T>;
  children: React.ReactNode;
} & TableCellProps): JSX.Element {
  return (
    <TableCell {...cellProps} sortDirection={sort.sortKey === columnKey ? sort.sortDir : false}>
      <TableSortLabel
        active={sort.sortKey === columnKey}
        direction={sort.sortKey === columnKey ? sort.sortDir : 'asc'}
        onClick={() => sort.toggle(columnKey)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
}

/**
 * Filtro por texto sobre los campos que la tabla decida.
 *
 * Normaliza acentos en los dos lados: quien busca "teologia" espera encontrar
 * "Teología", y quien escribe con acento también.
 */
export function useTextFilter<T>(
  rows: T[],
  fields: (row: T) => (string | undefined)[],
): {
  rows: T[];
  query: string;
  setQuery: (q: string) => void;
} {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => (query.trim() ? rows.filter((row) => matchesQuery(fields(row), query)) : rows),
    [rows, query, fields],
  );

  return { rows: filtered, query, setQuery };
}
